import { __awaiter, __decorate } from "tslib";
import { logger, Session, Connect } from '@genesislcap/foundation-comms';
import { CriteriaBuilder, ExpressionBuilder } from '@genesislcap/foundation-criteria';
import { formatDateTimeTimestamp } from '@genesislcap/foundation-utils';
import { attr, html, when, observable } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { foundationFileUploadStyles as styles } from './file-upload.styles';
import { foundationFileUploadTemplate as template } from './file-upload.template';
import { createFormData, downloadFile, uploadFiles } from './file-upload.utils';
const IMAGE_FILE_TYPES = ['image/png', 'image/bmp', 'image/gif', 'image/jpeg', 'image/jpg'];
const TEXT_FILE_TYPES = ['text/plain', 'text/csv'];
const OFFICE_FILE_TYPES = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
];
const FILE_SIZE_LIMIT = 104857600; // 100MiB as bytes
const cellTemplateColumn = html `
  ${when((x) => x.rowData, html `
      ${(x) => x.columnDefinition['type'] === 'DATETIME'
    ? formatDateTimeTimestamp(x.rowData[x.columnDefinition.columnDataKey])
    : x.rowData[x.columnDefinition.columnDataKey]}
    `)}
`;
/**
 * @tagname %%prefix%%-file-upload
 */
export class FileUpload extends FoundationElement {
    constructor() {
        super(...arguments);
        this.acceptedFormats = [];
        this.accept = '';
        this.fileSizeLimitBytes = FILE_SIZE_LIMIT;
        this.uploadedFilesResourceName = 'ALL_FILE_ATTACHMENTS';
        this.uploadEventName = 'gwf/attachment-handler/upload';
        this.gridFields = ''; // list of fieldNames to show in grid
        this.fileName = '';
        this.uploadedFilesList = [];
        this.filesGridColumnDefinitions = [];
    }
    acceptChanged() {
        let acceptedValues = [];
        if (this.accept.length > 0) {
            this.accept.split(',').forEach((el) => {
                if (el === 'image/*') {
                    acceptedValues = [...acceptedValues, ...IMAGE_FILE_TYPES];
                }
                else if (el === 'office/*') {
                    acceptedValues = [...acceptedValues, ...OFFICE_FILE_TYPES];
                }
                else if (el === 'text/*') {
                    acceptedValues = [...acceptedValues, ...TEXT_FILE_TYPES];
                }
                else {
                    acceptedValues.push(el);
                }
            });
        }
        this.acceptedFormats = acceptedValues;
    }
    fieldNameChanged() {
        this.setupGrid();
    }
    entityIdChanged() {
        this.setupGrid();
    }
    connectedCallback() {
        const _super = Object.create(null, {
            connectedCallback: { get: () => super.connectedCallback }
        });
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            _super.connectedCallback.call(this);
            if (!((_a = this.filesGridColumnDefinitions) === null || _a === void 0 ? void 0 : _a.length))
                this.setColumnDefs();
        });
    }
    setupGrid() {
        if (this.entityId !== undefined && this.fieldName !== undefined) {
            this.gridCriteria = new CriteriaBuilder()
                .withExpression(new ExpressionBuilder().withField(this.fieldName).withValue(this.entityId).build())
                .build();
            this.setRowData();
        }
    }
    handleStreamRowOperation(data) {
        let index;
        for (const row of data.ROW) {
            if (!row.DETAILS)
                continue;
            switch (row.DETAILS.OPERATION) {
                case 'INSERT':
                    this.uploadedFilesList.unshift(row);
                    break;
                case 'MODIFY':
                    index = this.uploadedFilesList.findIndex((e) => e.FILE_ATTACHMENT_ID === row.FILE_ATTACHMENT_ID);
                    this.uploadedFilesList.splice(index, 1, row);
                    break;
                case 'DELETE':
                    index = this.uploadedFilesList.findIndex((e) => e.FILE_ATTACHMENT_ID === row.FILE_ATTACHMENT_ID);
                    this.uploadedFilesList.splice(index, 1);
                    break;
                default:
                    logger.error(`Unsupported operation type: ${row.DETAILS.OPERATION}`);
            }
        }
    }
    setRowData() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.connect.stream(this.uploadedFilesResourceName, (response) => {
                var _a;
                if (response.SEQUENCE_ID === 1) {
                    this.uploadedFilesList = (_a = response.ROW) !== null && _a !== void 0 ? _a : [];
                }
                else {
                    this.handleStreamRowOperation(response);
                }
            }, (error) => {
                logger.error(error);
            }, { CRITERIA_MATCH: this.gridCriteria });
        });
    }
    setColumnDefs() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const metadata = yield this.connect.getMetadata(this.uploadedFilesResourceName);
            const columnDefs = [];
            if (this.gridFields.length > 0) {
                this.gridFields.split(' ').forEach((gridField) => {
                    var _a;
                    const field = (_a = metadata.FIELD) === null || _a === void 0 ? void 0 : _a.find((metadatField) => metadatField.NAME === gridField);
                    if (field) {
                        columnDefs.push(this.getColumnObject(field));
                    }
                    else if (gridField === 'DOWNLOAD' && this.downloadEventName) {
                        columnDefs.push(this.getDownloadColumn());
                    }
                });
            }
            else {
                (_a = metadata.FIELD) === null || _a === void 0 ? void 0 : _a.forEach((field) => {
                    columnDefs.push(this.getColumnObject(field));
                });
                if (this.downloadEventName) {
                    columnDefs.push(this.getDownloadColumn());
                }
            }
            this.filesGridColumnDefinitions = columnDefs;
        });
    }
    getDownloadColumn() {
        const prefix = this.tagName.toLowerCase().split('-')[0];
        return {
            columnDataKey: 'FILE_NAME',
            title: 'File',
            cellTemplate: html `
        <template>
          <div class="download-button-container">
            <${prefix}-button
              class="download-button"
              @click="${(x) => this.handleFileDownload(x.rowData)}"
            >
              DOWNLOAD
            </${prefix}-button>
          </div>
        </template>
      `,
        };
    }
    getColumnObject(column) {
        return {
            columnDataKey: column.NAME,
            title: column.NAME.toLowerCase()
                .split('_')
                .map((el) => el.charAt(0).toUpperCase() + el.slice(1))
                .join(' '),
            type: column.TYPE,
            cellTemplate: cellTemplateColumn,
        };
    }
    handleClick() {
        this.fileInput.value = null;
        this.fileInput.click();
    }
    onInputChange(e) {
        var _a, _b;
        const files = Array.from((_b = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.files) !== null && _b !== void 0 ? _b : []);
        this.onFilesSelected(files);
    }
    onFilesSelected(files) {
        if (files.length > 0 && this.acceptedFormats.length > 0) {
            for (const file of files) {
                if (!(this.accept.split(',').includes('.msg') && file.name && file.name.endsWith('msg'))) {
                    if (!this.acceptedFormats.includes(file.type)) {
                        alert(`Not a valid file type. Accepted Formats are ${this.accept}`);
                        return;
                    }
                }
                if (file.size > this.fileSizeLimitBytes) {
                    alert(`File size exceeds the file size limit of ${this.fileSizeLimitBytes} bytes`);
                    return;
                }
            }
            this.selectedFiles = files;
            this.fileName = this.selectedFiles.map((e) => e.name.trim()).join();
        }
    }
    handleFileUpload() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const formData = createFormData(this.selectedFiles);
                formData.append(this.uploadKey, this.entityId);
                const data = yield uploadFiles(this.uploadEventName, formData, this.session.getItem('authToken', 'session'));
                if (data.ERROR) {
                    logger.error(data.ERROR);
                    this.$emit('errorOut', data.ERROR);
                    return;
                }
                logger.info(data);
                this.clearSelection();
            }
            catch (ex) {
                logger.error(ex);
            }
        });
    }
    handleFileDownload(rowData) {
        downloadFile(this.downloadEventName(rowData), rowData.FILE_NAME, this.session.getItem('authToken', 'session'));
    }
    clearSelection() {
        this.fileName = '';
        this.selectedFiles = [];
    }
}
__decorate([
    observable
], FileUpload.prototype, "gridCriteria", void 0);
__decorate([
    Connect
], FileUpload.prototype, "connect", void 0);
__decorate([
    Session
], FileUpload.prototype, "session", void 0);
__decorate([
    attr
], FileUpload.prototype, "label", void 0);
__decorate([
    attr
], FileUpload.prototype, "accept", void 0);
__decorate([
    attr({ attribute: 'file-size-limit-bytes' })
], FileUpload.prototype, "fileSizeLimitBytes", void 0);
__decorate([
    attr({ attribute: 'uploaded-files-resource-name' })
], FileUpload.prototype, "uploadedFilesResourceName", void 0);
__decorate([
    attr({ attribute: 'upload-event-name' })
], FileUpload.prototype, "uploadEventName", void 0);
__decorate([
    attr({ attribute: 'field-name' })
], FileUpload.prototype, "fieldName", void 0);
__decorate([
    attr({ attribute: 'entity-id' })
], FileUpload.prototype, "entityId", void 0);
__decorate([
    attr({ attribute: 'upload-key' })
], FileUpload.prototype, "uploadKey", void 0);
__decorate([
    attr({ attribute: 'grid-fields' })
], FileUpload.prototype, "gridFields", void 0);
__decorate([
    observable
], FileUpload.prototype, "fileName", void 0);
__decorate([
    observable
], FileUpload.prototype, "uploadedFilesList", void 0);
__decorate([
    observable
], FileUpload.prototype, "filesGridColumnDefinitions", void 0);
// explicitly declaring for extensibility
export const foundationFileUploadShadowOptions = undefined;
export const defaultFileUploadConfig = {};
/**
 * The Foundation File Upload
 *
 * @public
 * @remarks
 * HTML Element: \<foundation-file-upload\>
 */
export const foundationFileUpload = FileUpload.compose(Object.assign({ baseName: 'file-upload', styles,
    template, shadowOptions: foundationFileUploadShadowOptions }, defaultFileUploadConfig));
